import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash2, Check, Repeat, NotebookPen, Clock, MessageSquare } from "lucide-react";
import {
  statusById, isTaskDone, latestProgress, efficiencyPct, isOverdue, isStale, isScheduledForLater,
  totalHoursLogged, effColor, fmtDate, fmtDateTime, hexToRgba, REPEAT_LABEL,
} from "./logbookUtils";

export const lbSelect = "w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white disabled:opacity-50";
export const Field = ({ label, children, className = "" }) => (
  <label className={`block space-y-1 ${className}`}>
    <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);

function Gauge({ pct }) {
  const v = pct === null ? 0 : Math.min(pct, 100);
  const c = 2 * Math.PI * 18;
  return (
    <div className="relative w-12 h-12 shrink-0" title={pct === null ? "No efficiency yet" : `Efficiency ${pct}%`}>
      <svg viewBox="0 0 44 44" className="w-12 h-12 -rotate-90">
        <circle cx="22" cy="22" r="18" fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle cx="22" cy="22" r="18" fill="none" stroke={effColor(pct)} strokeWidth="4" strokeDasharray={`${(v / 100) * c} ${c}`} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold" style={{ color: effColor(pct) }}>
        {pct === null ? "—" : `${pct}%`}
      </span>
    </div>
  );
}

function UpdateRow({ ctx, u, full }) {
  const { me, userName, act } = ctx;
  const [edit, setEdit] = useState(null);
  const mine = u.byUserId === me.id;
  if (edit) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Progress %"><Input type="number" min="0" max="100" value={edit.progressPct} onChange={(e) => setEdit({ ...edit, progressPct: e.target.value })} /></Field>
          <Field label="Hours"><Input type="number" min="0" step="0.5" value={edit.hoursLogged} onChange={(e) => setEdit({ ...edit, hoursLogged: e.target.value })} /></Field>
        </div>
        <Field label="Note"><Textarea value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></Field>
        <div className="flex gap-2">
          <Button size="sm" onClick={async () => {
            const ok = await act("edit_update", {
              updateId: u.id, progressPct: Math.max(0, Math.min(100, Number(edit.progressPct) || 0)),
              hoursLogged: Number(edit.hoursLogged) || null, note: edit.note.trim() || null,
            }, "Update edited");
            if (ok) setEdit(null);
          }}>Save</Button>
          <Button size="sm" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 text-xs py-1 border-t border-slate-100 first:border-t-0">
      <span className="font-mono text-slate-400 shrink-0">{fmtDateTime(u.date)}</span>
      <span className={`flex-1 text-slate-700 ${full ? "whitespace-pre-wrap" : ""}`}>
        {u.progressPct}% · {userName(u.byUserId)}{u.hoursLogged ? ` · ${u.hoursLogged}h` : ""}{u.note ? ` — ${u.note}` : ""}
      </span>
      {mine && (
        <span className="flex gap-2 shrink-0">
          <button type="button" className="text-blue-600 hover:underline" onClick={() => setEdit({ progressPct: u.progressPct, hoursLogged: u.hoursLogged || "", note: u.note || "" })}>edit</button>
          <button type="button" className="text-red-600 hover:underline" onClick={() => {
            if (window.confirm("Delete this update entry?")) act("delete_update", { updateId: u.id }, "Update deleted");
          }}>del</button>
        </span>
      )}
    </div>
  );
}

/* One Logbook task. showActions = the assignee's own board (log update, mark
   complete, renew); comments: "full" (Team board → View: every update + a
   comment box), "none", or the latest 4 (default). */
export default function LogbookTaskCard({ ctx, t, showActions, comments }) {
  const { me, data, userName, act } = ctx;
  const { statuses, users } = data;
  const [panel, setPanel] = useState(null);              // "update" | "complete" | "edit"
  const [form, setForm] = useState({});
  const [pendingStatus, setPendingStatus] = useState(null);
  const [comment, setComment] = useState("");
  const done = isTaskDone(statuses, t);
  const overdue = isOverdue(statuses, t);
  const stale = isStale(statuses, t);
  const progress = latestProgress(statuses, t);
  const eff = efficiencyPct(statuses, t);
  const st = statusById(statuses, t.status);
  const isAssigner = me.id === t.assignedBy;
  const canChangeStatus = me.id === t.assignedTo || me.id === t.assignedBy;
  const repeating = t.repeatType && t.repeatType !== "none";
  const ups = (t.updates || []).slice().reverse();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });

  const open = (p) => {
    if (panel === p) { setPanel(null); setPendingStatus(null); return; }
    if (p === "update") setForm({ progress, hours: "", note: "" });
    if (p === "complete") setForm({ est: "", actual: totalHoursLogged(t) || "" });
    if (p === "edit") setForm({
      title: t.title, description: t.description || "", assignedTo: t.assignedTo, dueDate: t.dueDate || "",
      est: t.estimatedHours || "", repeatOn: repeating, repeatType: repeating ? t.repeatType : "weekly", startDate: "",
    });
    setPanel(p);
  };

  const changeStatus = async (statusId) => {
    if (statusById(statuses, statusId).isDone) {
      // Moving into a done status needs the same hours confirmation as Mark complete.
      setPendingStatus(statusId);
      setForm({ est: "", actual: totalHoursLogged(t) || "" });
      setPanel("complete");
      return;
    }
    await act("update_task_status", { taskId: t.id, statusId }, "Status updated");
  };

  const submitComplete = async () => {
    const estimatedHours = Number(form.est) || null;
    if (!t.estimatedHours && !estimatedHours) return ctx.toastError("Enter an estimate so efficiency can be calculated");
    const actualHours = Number(form.actual) || null;
    if (!actualHours) return ctx.toastError("Enter the actual hours spent");
    const res = pendingStatus
      ? await act("update_task_status", { taskId: t.id, statusId: pendingStatus, estimatedHours, actualHours })
      : await act("mark_complete", { taskId: t.id, estimatedHours, actualHours });
    if (res) {
      ctx.toastOk(res.nextOccurrenceId ? "Task marked complete — next occurrence created" : "Task marked complete");
      setPanel(null); setPendingStatus(null);
    }
  };

  const submitEdit = async () => {
    if (!form.title.trim()) return ctx.toastError("Title required");
    const payload = {
      taskId: t.id, title: form.title.trim(), description: form.description.trim() || null,
      assignedTo: form.assignedTo, dueDate: form.dueDate || null, estimatedHours: Number(form.est) || null,
      repeatType: form.repeatOn ? form.repeatType : "none",
    };
    if (form.repeatOn && form.startDate) payload.startDate = form.startDate;
    if (await act("update_task", payload, "Task updated")) setPanel(null);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid={`lb-task-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 break-words">{t.title}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {canChangeStatus ? (
              <select
                className="h-7 rounded-full border px-2 text-xs font-medium"
                style={{ background: hexToRgba(st.color, 0.14), color: st.color, borderColor: hexToRgba(st.color, 0.4) }}
                value={t.status}
                onChange={(e) => changeStatus(e.target.value)}
                aria-label="Status"
                data-testid={`lb-task-status-${t.id}`}
              >
                {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : (
              <span className="rounded-full px-2 py-0.5 font-medium" style={{ background: hexToRgba(st.color, 0.14), color: st.color }}>{st.name}</span>
            )}
            {overdue && <span className="rounded-full bg-red-50 text-red-700 px-2 py-0.5 font-medium">Overdue</span>}
            {stale && !overdue && <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 font-medium">Needs update</span>}
            {isScheduledForLater(t, data.serverDate) && <span className="rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 font-medium inline-flex items-center gap-1"><Clock className="w-3 h-3" />Starts {fmtDate(t.availableFrom)}</span>}
            {repeating && <span className="rounded-full bg-violet-50 text-violet-700 px-2 py-0.5 font-medium inline-flex items-center gap-1"><Repeat className="w-3 h-3" />{REPEAT_LABEL[t.repeatType]}</span>}
            <span>· Assigned to <b className="text-slate-800">{userName(t.assignedTo)}</b></span>
            {t.assignedBy !== t.assignedTo && <span>· by {userName(t.assignedBy)}</span>}
            <span className="font-mono">· Due {fmtDate(t.dueDate)}</span>
            {t.estimatedHours ? <span className="font-mono">· Est {t.estimatedHours}h</span> : null}
          </div>
        </div>
        {done && <Gauge pct={eff} />}
      </div>
      {t.description && <div className="text-sm text-slate-600 whitespace-pre-wrap">{t.description}</div>}
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: done ? "#16a34a" : "#f59e0b" }} />
      </div>

      {(showActions && !done) || isAssigner ? (
        <div className="flex flex-wrap gap-2">
          {showActions && !done && <>
            <Button size="sm" onClick={() => open("update")} data-testid={`lb-log-update-${t.id}`}><NotebookPen className="w-3.5 h-3.5 mr-1" />Log update</Button>
            <Button size="sm" variant="outline" className="text-green-700 border-green-200" onClick={() => { setPendingStatus(null); open("complete"); }}><Check className="w-3.5 h-3.5 mr-1" />Mark complete</Button>
            {repeating && <Button size="sm" variant="outline" title="Generate the next occurrence now without marking this one complete"
              onClick={async () => { const r = await act("renew_task", { taskId: t.id }); if (r) ctx.toastOk(`Renewed — next occurrence due ${r.dueDate || ""}`); }}>
              <Repeat className="w-3.5 h-3.5 mr-1" />Renew</Button>}
          </>}
          {isAssigner && <>
            <Button size="sm" variant="outline" onClick={() => open("edit")}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => {
              if (window.confirm("Delete this task and its whole update history? This cannot be undone.")) act("delete_task", { taskId: t.id }, "Task deleted");
            }}><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
          </>}
        </div>
      ) : null}

      {panel === "update" && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Progress %"><Input type="number" min="0" max="100" value={form.progress} onChange={set("progress")} /></Field>
            <Field label="Hours logged today"><Input type="number" min="0" step="0.5" value={form.hours} onChange={set("hours")} /></Field>
          </div>
          <Field label="Note"><Textarea value={form.note} onChange={set("note")} placeholder="What got done, what's blocking…" /></Field>
          <Button size="sm" onClick={async () => {
            const ok = await act("add_update", {
              taskId: t.id, progressPct: Math.max(0, Math.min(100, Number(form.progress) || 0)),
              hoursLogged: Number(form.hours) || null, note: form.note.trim() || null,
            }, "Update logged");
            if (ok) setPanel(null);
          }}>Save update</Button>
        </div>
      )}

      {panel === "complete" && (
        <div className="rounded-md border border-green-200 bg-green-50/50 p-3 space-y-2">
          <p className="text-xs text-slate-600">To calculate efficiency, this needs an estimate and the actual hours it took.</p>
          <div className="grid grid-cols-2 gap-2">
            {!t.estimatedHours && <Field label="Estimated hours (this task had none)"><Input type="number" min="0" step="0.5" value={form.est} onChange={set("est")} placeholder="e.g. 4" /></Field>}
            <Field label="Actual hours spent"><Input type="number" min="0" step="0.5" value={form.actual} onChange={set("actual")} placeholder="e.g. 5" /></Field>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={submitComplete}>Confirm complete</Button>
            <Button size="sm" variant="outline" onClick={() => { setPanel(null); setPendingStatus(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {panel === "edit" && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
          <Field label="Task title"><Input value={form.title} onChange={set("title")} /></Field>
          <Field label="Description"><Textarea value={form.description} onChange={set("description")} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Reassign to">
              <select className={lbSelect} value={form.assignedTo} onChange={set("assignedTo")}>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Due date"><Input type="date" value={form.dueDate} onChange={set("dueDate")} /></Field>
            <Field label="Estimated hours"><Input type="number" min="0" step="0.5" value={form.est} onChange={set("est")} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={form.repeatOn} onCheckedChange={(v) => setForm({ ...form, repeatOn: !!v })} /> Repeated work
          </label>
          {form.repeatOn && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Work type">
                <select className={lbSelect} value={form.repeatType} onChange={set("repeatType")}>
                  <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                </select>
              </Field>
              <Field label="New start date"><Input type="date" value={form.startDate} onChange={set("startDate")} /></Field>
              <p className="col-span-2 text-[11px] text-slate-500">Leave Start Date blank to keep this task's current due date and only change Work Type for future renewals.</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={submitEdit}>Save changes</Button>
            <Button size="sm" variant="outline" onClick={() => setPanel(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {comments === "full" ? (
        <div className="space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Comments</div>
          {ups.length ? ups.map((u) => <UpdateRow key={u.id} ctx={ctx} u={u} full />) : <div className="text-xs text-slate-400">No comments logged yet.</div>}
          <div className="flex gap-2 pt-2">
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" className="min-h-[38px]" aria-label={`Comment on ${t.title}`} />
            <Button size="sm" variant="outline" onClick={async () => {
              const note = comment.trim();
              if (!note) return ctx.toastError("Write a comment first");
              // A comment is an update carrying only a note: progress stays, no hours added.
              if (await act("add_update", { taskId: t.id, progressPct: progress, hoursLogged: null, note }, "Comment saved")) setComment("");
            }}><MessageSquare className="w-3.5 h-3.5 mr-1" />Save</Button>
          </div>
        </div>
      ) : comments !== "none" && ups.length > 0 ? (
        <div>{ups.slice(0, 4).map((u) => <UpdateRow key={u.id} ctx={ctx} u={u} />)}</div>
      ) : null}
    </div>
  );
}
